/**
 * Traffic Light Review Configuration
 *
 * Defines the skills and categories for the Traffic Light (RAG) style
 * Coach Performance Reviews. Skills are organized by player type and category.
 */

// Skill categories for OUTFIELD players
export const OUTFIELD_SKILLS = {
  technical: [
    { key: "passing_possession", label: "Passing/Possession" },
    { key: "shooting_finishing", label: "Shooting/Finishing" },
    { key: "heading", label: "Heading" },
    { key: "1v1_attacking", label: "1v1 Attacking" },
    { key: "1v1_defending", label: "1v1 Defending" },
    { key: "crossing", label: "Crossing" },
    {
      key: "receiving_ball_retaining",
      label: "Receiving and ball retaining skills",
    },
    {
      key: "dribbling_running_with_ball",
      label: "Dribbling/Running with ball",
    },
    { key: "close_control_first_touch", label: "Close control/First touch" },
    { key: "weak_foot", label: "Weak foot" },
  ],
  tactical: [
    {
      key: "in_possession_create_space",
      label: "In Possession find/create space",
    },
    {
      key: "out_of_possession_close_space",
      label: "Out of Possession/close space",
    },
    {
      key: "winning_possession_reaction",
      label: "Winning possession/create space/reaction",
    },
    {
      key: "losing_possession_reaction",
      label: "Losing Possession/close space/reaction",
    },
    { key: "switching_play", label: "Switching Play" },
    { key: "combination_link_up_play", label: "Combination/link up play" },
    { key: "positional_understanding", label: "Positional understanding" },
  ],
  physical: [
    { key: "agility", label: "Agility" },
    { key: "coordination", label: "Co-ordination" },
    { key: "balance", label: "Balance" },
    { key: "speed", label: "Speed" },
    { key: "endurance", label: "Endurance" },
    { key: "strength", label: "Strength (U13-U18)" },
  ],
  mental: [
    {
      key: "intelligence_game_craft",
      label: "Intelligence – Game craft/knowledge",
    },
    { key: "vision", label: "Vision" },
    { key: "leadership", label: "Leadership" },
    { key: "decision_making", label: "Decision making" },
    { key: "composure", label: "Composure" },
    { key: "communication", label: "Communication" },
    { key: "coachability", label: "Coachability" },
    { key: "drive_desire", label: "Drive/Desire" },
    { key: "discipline", label: "Discipline" },
    { key: "confidence", label: "Confidence" },
  ],
};

// Skill categories for GOALKEEPER players
export const GOALKEEPER_SKILLS = {
  technical: [
    { key: "shot_stopping", label: "Shot stopping – various distances/angles" },
    { key: "footwork_positioning", label: "Good Footwork to adjust position" },
    {
      key: "start_position_stance",
      label: "Correct start position and body stance",
    },
    {
      key: "1v1s",
      label: "1v1's – spreads body, delays striker, diverts shots",
    },
    { key: "cross_balls", label: "Cross balls – catching/punching crosses" },
    {
      key: "movement_angles_support",
      label: "Movement to provide angles of support",
    },
    {
      key: "decision_making_build_phase",
      label: "Good decision making in build phase",
    },
    {
      key: "distribution_by_hand",
      label: "Distribution by hand accurately over various distances",
    },
    {
      key: "distribution_by_foot",
      label: "Distribution over long/short distances by foot",
    },
    { key: "pass_back_clearances", label: "Pass back clearances" },
  ],
  tactical: [
    {
      key: "adjusts_position_to_ball",
      label: "Adjusts pitch position in relation to the ball/team-mate",
    },
    {
      key: "provides_depth_recycle",
      label: "Provides depth to recycle/enable switch of play",
    },
    {
      key: "recognises_play_options",
      label: "Recognises when to play around, through or over",
    },
    {
      key: "anticipates_balls_over_top",
      label: "Anticipates balls over the top – sweeper keeper",
    },
    {
      key: "identifies_overloads",
      label: "Identifies overloads in build phase",
    },
    {
      key: "reacts_to_counterattack",
      label: "Reacts to opposition counterattack",
    },
    {
      key: "distribution_trigger_counter",
      label: "Good distribution to trigger counterattack opportunities",
    },
  ],
  physical: [
    { key: "agility_change_direction", label: "Agility/Change of Direction" },
    { key: "coordination", label: "Co-ordination" },
    { key: "balance", label: "Balance" },
    { key: "speed", label: "Speed" },
    { key: "endurance", label: "Endurance" },
    {
      key: "strength_upper_lower",
      label: "Strength (U13-U18) Upper body/Lower body",
    },
    { key: "other", label: "Other – Please specify" },
  ],
  mental: [
    {
      key: "intelligence_game_craft",
      label: "Intelligence – Game craft/knowledge",
    },
    { key: "vision", label: "Vision" },
    { key: "leadership", label: "Leadership" },
    { key: "decision_making", label: "Decision making" },
    { key: "composure", label: "Composure" },
    { key: "communication", label: "Communication" },
    { key: "coachability", label: "Coachability" },
    { key: "drive_desire", label: "Drive/Desire" },
    { key: "discipline", label: "Discipline" },
    { key: "confidence", label: "Confidence" },
  ],
};

// Category metadata for display
export const CATEGORIES = {
  technical: {
    key: "technical",
    label: "Technical",
    icon: "bi-gear",
    color: "#005596",
  },
  tactical: {
    key: "tactical",
    label: "Tactical",
    icon: "bi-diagram-3",
    color: "#17a2b8",
  },
  physical: {
    key: "physical",
    label: "Physical",
    icon: "bi-lightning",
    color: "#fd7e14",
  },
  mental: {
    key: "mental",
    label: "Mental",
    icon: "bi-brain",
    color: "#6f42c1",
  },
};

// RAG (Red, Amber, Green) rating options
export const RAG_OPTIONS = [
  {
    value: "green",
    label: "Green",
    color: "#28a745",
    description: "Strength / Exceeds expectations",
  },
  {
    value: "amber",
    label: "Amber",
    color: "#ffc107",
    description: "Developing / Meets expectations",
  },
  {
    value: "red",
    label: "Red",
    color: "#dc3545",
    description: "Needs improvement / Below expectations",
  },
];

// Player type options
export const PLAYER_TYPES = {
  outfield: { key: "outfield", label: "Outfield Player" },
  goalkeeper: { key: "goalkeeper", label: "Goalkeeper" },
};

// Goalkeeper position identifiers (for auto-detection)
export const GOALKEEPER_POSITIONS = [
  "goalkeeper",
  "gk",
  "goalie",
  "keeper",
  "goal keeper",
];

/**
 * Get skills configuration based on player type
 * @param {string} playerType - 'outfield' or 'goalkeeper'
 * @returns {Object} Skills configuration object
 */
export function getSkillsForPlayerType(playerType) {
  return playerType === "goalkeeper" ? GOALKEEPER_SKILLS : OUTFIELD_SKILLS;
}

/**
 * Detect player type based on position string
 * @param {string} position - Player's position from their profile
 * @returns {string} 'goalkeeper' or 'outfield'
 */
export function detectPlayerType(position) {
  if (!position) return "outfield";

  const normalizedPosition = position.toLowerCase().trim();
  const isGoalkeeper = GOALKEEPER_POSITIONS.some((gkPos) =>
    normalizedPosition.includes(gkPos),
  );

  return isGoalkeeper ? "goalkeeper" : "outfield";
}

/**
 * Create empty traffic light data structure for a player type
 * @param {string} playerType - 'outfield' or 'goalkeeper'
 * @returns {Object} Empty traffic light data object with all skills set to null
 */
export function createEmptyTrafficLightData(playerType) {
  const skills = getSkillsForPlayerType(playerType);
  const data = {
    player_type: playerType,
  };

  Object.keys(skills).forEach((category) => {
    data[category] = {};
    skills[category].forEach((skill) => {
      data[category][skill.key] = null;
    });
  });

  return data;
}

/**
 * Validate traffic light data structure
 * @param {Object} data - Traffic light data to validate
 * @returns {boolean} True if valid structure
 */
export function validateTrafficLightData(data) {
  if (!data || typeof data !== "object") return false;
  if (
    !data.player_type ||
    !["outfield", "goalkeeper"].includes(data.player_type)
  ) {
    return false;
  }

  const skills = getSkillsForPlayerType(data.player_type);
  const categories = Object.keys(skills);

  return categories.every((category) => {
    return data[category] && typeof data[category] === "object";
  });
}

/**
 * Calculate completion percentage for traffic light data
 * @param {Object} data - Traffic light data object
 * @returns {number} Percentage of skills that have been rated (0-100)
 */
export function calculateCompletionPercentage(data) {
  if (!validateTrafficLightData(data)) return 0;

  const skills = getSkillsForPlayerType(data.player_type);
  let total = 0;
  let completed = 0;

  Object.keys(skills).forEach((category) => {
    skills[category].forEach((skill) => {
      total++;
      if (data[category] && data[category][skill.key]) {
        completed++;
      }
    });
  });

  return total > 0 ? Math.round((completed / total) * 100) : 0;
}

/**
 * Get summary statistics for traffic light data
 * @param {Object} data - Traffic light data object
 * @returns {Object} Summary with counts for each rating
 */
export function getTrafficLightSummary(data) {
  if (!validateTrafficLightData(data)) {
    return { green: 0, amber: 0, red: 0, unrated: 0, total: 0 };
  }

  const skills = getSkillsForPlayerType(data.player_type);
  const summary = { green: 0, amber: 0, red: 0, unrated: 0, total: 0 };

  Object.keys(skills).forEach((category) => {
    skills[category].forEach((skill) => {
      summary.total++;
      const rating = data[category]?.[skill.key];
      if (rating === "green") summary.green++;
      else if (rating === "amber") summary.amber++;
      else if (rating === "red") summary.red++;
      else summary.unrated++;
    });
  });

  return summary;
}

export default {
  OUTFIELD_SKILLS,
  GOALKEEPER_SKILLS,
  CATEGORIES,
  RAG_OPTIONS,
  PLAYER_TYPES,
  GOALKEEPER_POSITIONS,
  getSkillsForPlayerType,
  detectPlayerType,
  createEmptyTrafficLightData,
  validateTrafficLightData,
  calculateCompletionPercentage,
  getTrafficLightSummary,
};
